import React, { useEffect, useState } from 'react'
import DefaultSortableRenderItem from '../generic/smartSortable/GenericSortableRenderItem'
import SmartSortableContainer from '../generic/smartSortable/SmartSortableContainer'
import { SmartSelect } from '../generic/smartSelect'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { ChangeCircleSharp, SaveRounded } from '@mui/icons-material'
import { apiGet } from '../generic/Api_Functions'
import { SmartDialog } from '../generic/utilities/SmartDialog'
import createDialogTitle from '../generic/utilities/dialogUtil'

// Import typedefs from another file
/**
 * @typedef {import('../generic/utilities/objectStructures/sequencer/sequencer').Sequencer} Sequencer Sequencer object
 * @typedef {import('../generic/utilities/objectStructures/sequencer/sequencer').FabricationOrderSequencer} FabricationOrderSequencer FabricationOrder object
*/

/**
 *
 * @param {object} props
 * @param {Sequencer[]} props.sequencerList
 * @param {function(Sequencer[])} props.onChangeSequencerList
 * @param {function(FabricationOrder)} props.onSortChanged
 * @param {String} props.selectedAsset
 * @param {function(String)} props.onSelectedAssetChange
 * @returns
 */
const ArrangeSequence = ({ sequencerList, onChangeSequencerList, onSortChanged, selectedAsset, onSelectedAssetChange }) => {
  const { t_ } = useSmartTranslation()
  /** @type {[String[], React.Dispatch<SetStateAction<String[]>>]} */
  const [availableAssetList, setAvailableAssetList] = useState([])
  const [openChangeAssetLisDialog, setOpenChangeAssetListDialog] = useState(false)
  const [newSelectedAsset, setNewSelectedAsset] = useState('')
  /** @type {[FabricationOrderSequencer, React.Dispatch<SetStateAction<FabricationOrderSequencer>>]} */
  const [currentSelectedFabricationOrder, SetCurrentSelectedFabricationOrder] = useState({})

  useEffect(() => {
    if (!newSelectedAsset || newSelectedAsset === '') {
      setNewSelectedAsset(selectedAsset)
    }
  }, [selectedAsset, newSelectedAsset])

  /**
   * @param {object} props
   * @param {FabricationOrder} props.item
   * @returns
  */
  const ArrangeItem = ({ item }) => {
    return <DefaultSortableRenderItem labels={[item.name + ' - ' + item.jobReference.name]} itemId={item?.id} icon={<ChangeCircleSharp />} onIconClick={() => onIconClick(item)} />
  }

  const onIconClick = async (item) => {
    const data = await apiGet('availableDevicesByJob/' + item.jobReference.id)
    setOpenChangeAssetListDialog(true)
    setAvailableAssetList(data)
    SetCurrentSelectedFabricationOrder(item)
  }

  const sortChanged = (items) => {
    items.forEach((item, index) => {
      item.order = index + 1
    })
    // Call the sortingChange function with the updated items
    if (onSortChanged && typeof onSortChanged === 'function') {
      onSortChanged(items)
    }
  }

  const onAcceptSelectedAssetChange = () => {
    const newSequenceList = structuredClone(sequencerList)

    for (let count = 0; count < newSequenceList.length; count++) {
      const removeFabricationOrderId = newSequenceList[count].fabricationOrderSequencerList.findIndex(fo => fo.id === currentSelectedFabricationOrder.id && fo.jobReference.id === currentSelectedFabricationOrder.jobReference.id)
      if (removeFabricationOrderId > -1) {
        newSequenceList[count].fabricationOrderSequencerList.splice(removeFabricationOrderId, 1)
        for (let count2 = removeFabricationOrderId; count2 < newSequenceList[count].fabricationOrderSequencerList.length; count2++) {
          newSequenceList[count].fabricationOrderSequencerList[count2].order--
        }
        break
      }
    }
    const sequenceIndex = newSequenceList.findIndex((sequencer) => sequencer.assetName === newSelectedAsset)
    const copyCurrentSelectedFabricationOrder = structuredClone(currentSelectedFabricationOrder)
    copyCurrentSelectedFabricationOrder.assetName = newSelectedAsset
    if (sequenceIndex === -1) {
      copyCurrentSelectedFabricationOrder.order = 1
      newSequenceList.push({ startDate: null, endDate: null, assetName: newSelectedAsset, fabricationOrderSequencerList: [copyCurrentSelectedFabricationOrder] })
    } else {
      copyCurrentSelectedFabricationOrder.order = newSequenceList[sequenceIndex].fabricationOrderSequencerList.length + 1
      newSequenceList[sequenceIndex].fabricationOrderSequencerList.push(copyCurrentSelectedFabricationOrder)
    }
    onChangeSequencerList(newSequenceList)
    onSelectedAssetChange(newSelectedAsset)
    setOpenChangeAssetListDialog(false)
  }

  const onAssetChange = (value) => {
    setNewSelectedAsset(value)
  }

  return (
    <>
      <SmartDialog
        setOpen={openChangeAssetLisDialog}
        cancelCallback={() => setOpenChangeAssetListDialog(false)}
        acceptCallback={onAcceptSelectedAssetChange}
        title={createDialogTitle({ title: t_('Change Asset?'), icon: SaveRounded })}
        renderComponent={
          <div style={{ marginTop: '1rem' }}>
            <SmartSelect
              label={t_('Select option')}
              value={newSelectedAsset}
              selectableOptions={availableAssetList}
              onChange={onAssetChange}
            />
          </div>
        }

      />
      {sequencerList && sequencerList.length > 0
        ? (
          <>
            <div style={{ marginTop: '1rem' }}>
              <SmartSelect
                label={t_('Asset')}
                value={selectedAsset}
                selectableOptions={sequencerList?.map(sequence => sequence.assetName)}
                onChange={onSelectedAssetChange}
              />
            </div>
            <div style={{ width: '100%', marginTop: '1rem' }}>
              <SmartSortableContainer
                itemList={sequencerList.find((sequencer) => sequencer.assetName === selectedAsset)?.fabricationOrderSequencerList}
                itemId={(item) => item.id + '-' + item.jobReference.id}
                renderItem={ArrangeItem}
                onSortChanged={sortChanged}
              />
            </div>
          </>)
        : null}
    </>
  )
}

export default ArrangeSequence
